import { memo } from "react";
import { Loader2 } from "lucide-react";
import useProductDetail from "@/app/lib/features/productDetail/productDetail.hook";
import { trackProductClick } from "@/app/lib/tracking/interactions";

interface ProductSizesProps {
  selectedColorIndex: number;
  selectedSizeIndex: number;
  loadingSizeIndex: number | null;
  onSizeChange: (sizeIndex: number) => void;
}

const ProductSizes = memo(
  ({
    selectedColorIndex,
    selectedSizeIndex,
    loadingSizeIndex,
    onSizeChange,
  }: ProductSizesProps) => {
    const { product } = useProductDetail();

    if (!product?.variants_by_color?.length) return null;

    const selectedColor = product.variants_by_color[selectedColorIndex];

    if (!selectedColor?.sizes?.length) return null;

    const currentSize = selectedColor.sizes[selectedSizeIndex];

    const handleSizeClick = (
      index: number,
      isOutOfStock: boolean,
      isLoadingThis: boolean,
    ) => {
      if (isOutOfStock || isLoadingThis) return;
      if (product?.id) trackProductClick(product.id);
      onSizeChange(index);
    };

    return (
      <div className="space-y-3 mb-6">
        <div className="flex items-center gap-2 text-sm">
          <span className="font-medium">Beden:</span>
          {currentSize && (
            <span className="text-main font-medium">{currentSize.size}</span>
          )}
        </div>
        <div className="grid grid-cols-8 gap-2">
          {selectedColor.sizes.map((size, index) => {
            const isSelected = index === selectedSizeIndex;
            const isOutOfStock = size.stock === 0;
            const isLoadingThis = loadingSizeIndex === index;

            return (
              <button
                key={index}
                type="button"
                onClick={() =>
                  handleSizeClick(index, isOutOfStock, isLoadingThis)
                }
                disabled={isOutOfStock || isLoadingThis}
                className={`
                relative h-9 border-2 rounded-lg font-medium text-sm transition-all duration-200 flex items-center justify-center
                ${
                  isOutOfStock
                    ? "border-gray-200 bg-gray-100 text-gray-400 cursor-not-allowed line-through"
                    : isLoadingThis
                      ? "border-main bg-main/10 text-main"
                      : isSelected
                        ? "border-main bg-main text-white shadow-md"
                        : "border-gray-300 bg-white text-gray-700 hover:border-main hover:text-main hover:shadow-sm"
                }
              `}
              >
                {isLoadingThis ? (
                  <Loader2 size={16} className="animate-spin" />
                ) : (
                  size.size
                )}
              </button>
            );
          })}
        </div>
      </div>
    );
  },
);

ProductSizes.displayName = "ProductSizes";

export default ProductSizes;
